import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import {
  deleteGlobalExternalCertificationCategory,
  getAllExternalCertificationCategories,
} from '@/services/external_certifications.service'
import { UserStoreContext } from '@/stores/UserStore'
import { cn, toast } from '@/utils/helpers'

import AddEditCategoryModal from './AddEditCategoryModal'

const ExternalCertificationCategories = observer(() => {
  const { isXverseAdmin } = useContext(UserStoreContext)

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showModal, setShowModal] = useState(false)
  const [editCategory, setEditCategory] = useState(null)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { data: categories, isPending } = useQuery({
    queryKey: ['external-certification-categories-admin', sorting, pagination, searchTerm],
    queryFn: () => getAllExternalCertificationCategories({ sorting, pagination, searchTerm }),
    placeholderData: keepPreviousData,
  })

  const { isPending: pendingDelete, mutate: deleteCategory } = useMutation({
    mutationFn: (data) => deleteGlobalExternalCertificationCategory(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Category deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['external-certification-categories-admin'] })
    },
  })

  // Search shrinks the result set, so a deep page can 404 from the paginator.
  // Reset to page 0 in the same handler so React batches the state updates.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <Layout breadcrumbs={['Admin', 'External Credential Categories']}>
      <Layout.Heading title="External Credential Categories" />

      <div className="flex flex-row items-center gap-2">
        {searchTerm && (
          <Button
            className="visible sm:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button
          className="visible sm:hidden"
          disabled={!isXverseAdmin}
          onClick={() => setShowModal(true)}
        >
          Add Category
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 sm:flex-row">
          <Input
            className="w-full sm:w-64"
            placeholder="Search for Category"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm && (
            <Button
              className="hidden sm:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button
          className="hidden sm:block"
          disabled={!isXverseAdmin}
          onClick={() => setShowModal(true)}
        >
          Add Category
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!isXverseAdmin}
                onClick={() => {
                  setEditCategory(row.original)
                  setShowModal(true)
                }}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            accessorKey: 'description',
            header: ({ column }) => <SortableColumnHeader column={column} label="Description" />,
            cell: ({ cell }) => (
              <span
                className={cn(
                  'max-w-[200px] truncate text-ellipsis',
                  !cell.getValue() && 'text-muted-foreground',
                )}
              >
                {cell.getValue() || 'No description'}
              </span>
            ),
          },
          {
            accessorKey: 'categoryType',
            header: 'Type',
            cell: ({ cell }) => (
              <Badge variant="outline">
                {cell.getValue() === 'Entity' ? 'Account' : cell.getValue()}
              </Badge>
            ),
          },
          {
            accessorKey: 'isActive',
            header: 'Status',
            cell: ({ cell }) => (
              <Badge variant={cell.getValue() ? 'success' : 'secondary'}>
                {cell.getValue() ? 'Active' : 'Inactive'}
              </Badge>
            ),
          },
          {
            accessorKey: 'entity',
            header: 'Account',
            cell: ({ row }) => (
              <Badge variant={row.original.entity ? 'default' : 'outline'}>
                {row.original.entity?.name}
              </Badge>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="outline"
                onClick={() => {
                  setEditCategory(row.original)
                  setShowModal(true)
                }}
              >
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete the category: ${row.original.name}?`}
                confirmTitle="Delete Category"
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="destructive"
                loading={pendingDelete}
                onClick={() => deleteCategory(row.original)}
              >
                Delete
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={categories?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={categories?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showModal && (
        <AddEditCategoryModal
          editCategory={editCategory}
          resetAndClose={() => {
            setShowModal(false)
            setEditCategory(null)
            queryClient.invalidateQueries({
              queryKey: ['external-certification-categories-admin'],
            })
          }}
        />
      )}
    </Layout>
  )
})

export default ExternalCertificationCategories
